import { Box, Breadcrumbs, Button, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import ProductCard from "../components/downloads-page/ProductCard";
import { convertToURL, getSortedDocs } from "../components/downloads-page/downloadsHelpers";
import { useNavigate, useParams } from "react-router-dom";
import ProductInformation from "../components/downloads-page/ProductInformation";
import { useEffect, useState } from "react";
import { getProductCategories, getProductContent, getProductLines, getProductsList } from "../helpers/apiCalls";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Link as RouterLink } from 'react-router-dom';

export default function Downloads() {
  let { productCategory, productLine, product, version } = useParams()

  const [data, setData] = useState({ docs: [] });
  const [loading, setLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [error, setError] = useState(false)

  let navigate = useNavigate()

  const retrieveProductContents = async (page = 0) => {
    let response = await getProductContent(encodeURIComponent(product), page, false);

    if (response.status === 200) {
      setData(response.data)

      if (response.data.docs.length < 1) {
        setError(true)
      }
    }
    else {
      setError(true)
    }

    setProductLoading(false);
  }

  const retrieveProductsFromProductLine = async (page = 0) => {
    let response = await getProductsList(encodeURIComponent(productLine), page)

    if (response.status === 200) {
      setData(response.data)

      if (response.data.docs.length < 1) {
        setError(true)
      }
    }
    else {
      setError(true)
    }

    setLoading(false);
  }

  const retrieveProductLines = async (page = 0) => {
    let response = await getProductLines(encodeURIComponent(productCategory), page)

    if (response.status === 200) {
      setData(response.data)

      if (response.data.docs.length < 1) {
        setError(true)
      }
    }
    else {
      setError(true)
    }

    setLoading(false);
  }

  const retrieveCategories = async (page = 0) => {
    let response = await getProductCategories(page);

    if (response.status === 200) {
      setData(response.data)

      if (response.data.docs.length < 1) {
        setError(true)
      }
    }
    else {
      setError(true)
    }

    setLoading(false);
  }

  const getPage = (page) => {
    setLoading(true)

    if (product) {
      retrieveProductContents(page);
    }
    else if (productLine) {
      retrieveProductsFromProductLine(page)
    }
    else if (productCategory) {
      retrieveProductLines(page)
    }
    else {
      retrieveCategories(page);
    }
  }

  useEffect(() => {
    setError(false);

    if (product) {
      setProductLoading(true);
      retrieveProductContents();
    }
    else if (productLine) {
      setLoading(true);
      retrieveProductsFromProductLine()
    }
    else if (productCategory) {
      setLoading(true);
      retrieveProductLines()
    }
    else {
      setLoading(true);
      retrieveCategories();
    }
  }, [productCategory, productLine, product])

  const ProductCards = () => {
    let path = [];

    if (productCategory && !(productLine)) {
      path = [productCategory]
    }
    else if (productCategory && productLine && !product) {
      path = [productCategory, productLine]
    }

    return (
      getSortedDocs(data.docs).map(item => {
        return (
          <ProductCard
            key={item.id}
            title={item.title}
            path={[...path, productLine ? item.productid : item.title]}
            images={item.image}
          />
        )
      })
    )
  }

  return (
    <Stack
      sx={{
        height: '100pv',
        mb: 2
      }}
      spacing={3}
    >
      {
        error ?
          <ErrorRetrieving />
          :
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '& >*': {
                  mr: 1
                }
              }}
            >
              {
                !productCategory ?
                  <Typography variant='h5'>
                    Select your product:
                  </Typography>
                  :
                  <Breadcrumbs
                    sx={{
                      pt: 1,
                      pb: 1
                    }}
                  >
                    {
                      productCategory &&
                      <Typography
                        onClick={() => navigate('/')}
                        sx={{
                          '&:hover': {
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }
                        }}
                      >
                        Products
                      </Typography>
                    }
                    {
                      productCategory &&
                      <Typography
                        onClick={() => navigate(convertToURL([productCategory]))}
                        sx={{
                          color: !product && !productLine ? 'text.primary' : '',
                          '&:hover': {
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }
                        }}
                      >
                        {productCategory}
                      </Typography>
                    }
                    {
                      productCategory && productLine &&
                      <Typography
                        onClick={() => navigate(convertToURL([productCategory, productLine]))}
                        sx={{
                          color: !product ? 'text.primary' : '',
                          '&:hover': {
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }
                        }}
                      >
                        {productLine}
                      </Typography>
                    }

                    {
                      productCategory && productLine && product &&
                      <Typography
                        sx={{
                          color: 'text.primary',
                        }}
                      >
                        {data.docs.length > 0 && data.docs[0] ? `${data.docs[0].title} (${data.docs[0].productid})` : product}
                      </Typography>
                    }
                  </Breadcrumbs>
              }
              {
                data.totalPages > 1 &&
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <IconButton
                    disabled={!data.hasPrevPage}
                    onClick={() => getPage(data.prevPage)}
                  >
                    <ArrowBack />
                  </IconButton>
                  Page {data.page} of {data.totalPages}
                  <IconButton
                    disabled={!data.hasNextPage}
                    onClick={() => getPage(data.nextPage)}
                  >
                    <ArrowForward />
                  </IconButton>
                </Box>
              }
            </Box>
            {
              loading ?
                <PageLoadingSkeleton />
                :
                (
                  !product &&
                  <Stack
                    spacing={2}
                    direction={{ xs: 'column', sm: 'row' }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <ProductCards />
                  </Stack>
                )
            }
            {
              productLoading ?
                <PageLoadingSkeleton />
                :
                productCategory && productLine && product && data.docs.length > 0 && data.docs[0]?.content &&
                <ProductInformation
                  product={data.docs[0]}
                />
            }
          </>
      }
    </Stack>
  )
}

const ProductCardSkeleton = () => (
  <Skeleton
    variant='rectangular'
    width={347}
    height={280}
    sx={{
      borderRadius: .5
    }}
  />
)

const ErrorRetrieving = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        textAlign: 'center'
      }}
    >
      <Typography variant='h4'>
        Sorry, unable to retrieve the page.
      </Typography>
      <Typography variant='h6' sx={{ mt: 1, color: 'text.secondary' }}>
        Refresh the page to try again or
      </Typography>
      <Box
        sx={{
          mt: 1
        }}
      >
        <Button to='/' size='large' variant='contained' component={RouterLink}>
          Return Home
        </Button>
      </Box>
    </Box >
  )
}

const PageLoadingSkeleton = () => (
  <Stack
    spacing={2}
    direction={{ xs: 'column', sm: 'row' }}
    flexWrap="wrap"
    useFlexGap
  >
    <ProductCardSkeleton />
    <ProductCardSkeleton />
    <ProductCardSkeleton />
    <ProductCardSkeleton />
    <ProductCardSkeleton />
    <ProductCardSkeleton />
  </Stack>
)